import axios from 'axios';
import React, { useState, useEffect } from 'react';
import {
    Link,
    useParams
} from "react-router-dom";
import { useAuth } from '@zones-digital-platform/react-oidc-auth';
import $ from 'jquery';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faExclamationTriangle, faPlus, faMinus, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { DateTime } from 'luxon';
import StatusDropdown from './StatusDropdown';
import ConfirmationModal from './ConfirmationModal';
import "../css/spinner.css";

const poUrl = process.env.REACT_APP_INTERNAL_PO;
const postUrl = process.env.REACT_APP_INTERNAL_UO;
const carrierUrl = process.env.REACT_APP_INTERNAL_COURIERS;
const dataApi = process.env.REACT_APP_DATA_FIELD;
const mapKey = process.env.REACT_APP_G_KEY;

const Post = () => {
    const { id } = useParams(); // get the id from the URL parameter
    const [post, setPost] = useState({}); // initialize state for the post
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [loadingIcon, setLoadingIcon] = useState('');
    const [courierLoader, setCourierLoader] = useState(false);
    const [couriers, setCouriers] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [initialStatuses, setInitialStatuses] = useState([]);
    const [disabledLines, setDisabledLines] = useState({});
    const [disabledTimes, setDisabledTimes] = useState({});
    const [isAssetChecked, setIsAssetChecked] = useState(false);
    const { isAuthenticated, getToken } = useAuth();
    var count = 0;
    var itemNum = 0;
    var itemIndex = 0;
    var serialIndex = 0;
    var qtyErr = false;
    var qtyZero = false;
    var hasError = false;
    var shipStatus = '';

    const checkDisabledDropdowns = () => {
        const keys = Object.keys(localStorage);
        const currentDisabled = {};
        const disabledTimes = {};

        keys.forEach(key => {
            const [storedId, lineIndex, timestamp] = key.split('_');
            const remainingTime = parseInt(timestamp) - Date.now();

            if (storedId === id && remainingTime > 0) {
                currentDisabled[lineIndex] = true; // Mark this lineIndex as disabled
                disabledTimes[lineIndex] = remainingTime; // Store remaining time
            }
        });

        setDisabledLines(currentDisabled);
        setDisabledTimes(disabledTimes); // Store remaining times
    };

    useEffect(() => {
        if (isAuthenticated) {
            fetchData();
        }
        checkDisabledDropdowns();
    }, [isAuthenticated, id]);

    const fetchData = async () => {
        if (!isAuthenticated) {
            console.log('User not authenticated, skipping fetch');
            return;
        }

        try {
            const response = await fetch(`${poUrl}/${id}`, {
                headers: {
                    'Authorization': `Bearer ${getToken()}`,
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            const postData = dataApi ? data[dataApi] : data;
            setPost(postData);

            // Store initial statuses
            const initialStatuses = postData.orderLevels[0].orderLines.map(line =>
                line.lineDetails.map(detail => detail.status)
            );
            setInitialStatuses(initialStatuses);
        } catch (error) {
            console.error('Error fetching post data:', error);
        }
    };

    const handleChange = (e, lineIndex, detailIndex) => {
        $('.qty-split').removeClass('bg-rose-100 text-rose-500 border-rose-500').addClass('bg-gray-200 text-gray-900 border-transparent');

        const { name, value, type, checked } = e.target;

        if (type === 'checkbox') {
            setIsAssetChecked({
                ...isAssetChecked,
                [lineIndex]: checked,
            });
        } else {
            const updatedPost = { ...post };
            const updatedDetail = { ...updatedPost.orderLevels[0].orderLines[lineIndex].lineDetails[detailIndex] };

            updatedDetail[name] = value;
            updatedPost.orderLevels[0].orderLines[lineIndex].lineDetails[detailIndex] = updatedDetail;

            setPost(updatedPost);
        }
    };

    const handleCopyValues = () => {
        // copy all values from first row
        const firstLineDetails = post.orderLevels[0].orderLines[0].lineDetails.map(detail => ({
            status: detail.status || '',
            trackingNumber: detail.trackingNumber || '',
            carrier: detail.carrier || '',
            deliveryDate: detail.deliveryDate || '',
            signedBy: detail.signedBy || ''
        }));

        const allValuesBlank = firstLineDetails.every(detail =>
            !detail.status &&
            !detail.trackingNumber &&
            !detail.carrier &&
            !detail.deliveryDate &&
            !detail.signedBy
        );

        // if all values are blank, do nothing
        if (allValuesBlank) {
            return;
        }

        const updatedOrderLevels = post.orderLevels.map(orderLevel => ({
            ...orderLevel,
            orderLines: orderLevel.orderLines.map(line => ({
                ...line,
                lineDetails: line.lineDetails.length > 0
                    ? line.lineDetails.map((detail, detailIndex) => {
                        const firstDetail = firstLineDetails[detailIndex] || {};
                        return {
                            ...detail,
                            status: firstDetail.status,
                            trackingNumber: firstDetail.trackingNumber,
                            carrier: firstDetail.carrier,
                            deliveryDate: firstDetail.deliveryDate,
                            signedBy: firstDetail.signedBy,
                        };
                    })
                    : [{
                        status: firstLineDetails[0].status,
                        trackingNumber: firstLineDetails[0].trackingNumber,
                        carrier: firstLineDetails[0].carrier,
                        deliveryDate: firstLineDetails[0].deliveryDate,
                        signedBy: firstLineDetails[0].signedBy,
                    }]
            }))
        }));

        setPost(prevPost => ({
            ...prevPost,
            orderLevels: updatedOrderLevels,
        }));
    };

    async function convertToUtcTime(inputDateTime, address) {
        const gKey = mapKey;
        const geoCodeURL = `https://maps.googleapis.com/maps/api/geocode/json?address=${address}&sensor=false&key=${gKey}`;

        try {
            const geoCodeResponse = await fetch(geoCodeURL);
            const geoCodeData = await geoCodeResponse.json();

            if (!geoCodeData.results || geoCodeData.results.length === 0) {
                throw new Error('Invalid address');
            }

            const location = geoCodeData.results[0].geometry.location;
            const timeStamp = Math.floor(DateTime.fromJSDate(inputDateTime).toSeconds());

            const timeZoneURL = `https://maps.googleapis.com/maps/api/timezone/json?location=${location.lat},${location.lng}&timestamp=${timeStamp}&key=${gKey}`;

            const timeZoneResponse = await fetch(timeZoneURL);
            const timeZoneData = await timeZoneResponse.json();

            const targetTimeZone = timeZoneData.timeZoneId;
            const inputDateTimeLuxon = DateTime.fromJSDate(inputDateTime).setZone(targetTimeZone);
            //   console.log(inputDateTimeLuxon.toISO());

            return inputDateTimeLuxon.toISO();

        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    const handleModalSave = () => {
        let statusChanged = false;

        post.orderLevels[0].orderLines.forEach((line, lineIndex) => {
            line.lineDetails.forEach((detail, detailIndex) => {
                const initialStatus = initialStatuses[lineIndex][detailIndex];
                const currentStatus = detail.status;
                if (currentStatus !== initialStatus) {
                    statusChanged = true;
                }
                if (line.isAsset && (detail.status.toLowerCase() == 'shipped' || detail.status.toLowerCase() == 'delivered' || detail.status.toLowerCase() == 'exception' || detail.status.toLowerCase() == 'refused')) {
                    if (!detail.serialNumber) {
                        hasError = true;
                        shipStatus = detail.status;
                        serialIndex = lineIndex;
                    }
                }
                if (line.isAsset && (detail.status.toLowerCase() == 'shipped')) {
                    if (!detail.assetTag && !isAssetChecked) {
                        hasError = true;
                        shipStatus = detail.status;
                        serialIndex = lineIndex;
                    }
                }
            });
        });

        if (statusChanged) {
            if (hasError) {
                setLoadingIcon('');
                setErrorMessage('Serial Number and Asset Tag are required for status: ' + shipStatus);
                $('.asset-' + serialIndex).removeClass('bg-gray-200 text-gray-900 border-transparent').addClass('bg-rose-100 text-rose-500 border-rose-500');
                $('.asset-checkbox-' + serialIndex).removeClass('hidden');
                return;
            } else {
                setShowModal(true);
                return;
            }
        } else {
            handleSave();
        }
    }

    const handleSave = async () => {
        // remove error msg
        setErrorMessage('');
        // set spinner
        setLoadingIcon(' ');

        checkDisabledDropdowns();

        const postData = {
            poNumber: post.poNumber,
            extCustomerId: post.extCustomerId,
            extCustomerMappingId: post.extCustomerMappingId,
            orderLinesDetail: []
        };

        $('.qty-split').each(function () {
            if ($(this).val() === '0') {
                qtyZero = true;
            }
        });

        $('.add-btn .bg-rose-500').hide();

        post.orderLevels[0].orderLines.forEach((line, lIndex) => {
            let qtyCnt = 0;
            let isFirstDetail = true; // Flag to track the first lineDetail

            line.lineDetails.forEach((detail) => {
                if (Object.values(detail).some(val => val)) {

                    if (line.quantity > 1 && (!detail.hasOwnProperty('quantity') || detail.quantity === '')) {
                        if (line.lineDetails.length === 1) {
                            detail.quantity = line.quantity;
                        } else {
                            detail.quantity = '1'; // Set default value to '1' if quantity is empty or not present
                        }
                    }
                    const quantity = parseInt(detail.quantity);
                    if (line.quantity > 1) {
                        qtyCnt += quantity;
                    }

                    const inputDateTime = DateTime.fromISO(detail.deliveryDate).toJSDate();
                    const apiAddress = post.orderLevels[0].shipToCity + ', ' + post.orderLevels[0].shipToCountry;

                    if (detail.deliveryDate !== undefined && detail.deliveryDate !== null && detail.deliveryDate !== '') {
                        convertToUtcTime(inputDateTime, apiAddress)
                            .then(result => {
                                const formattedDateTime = DateTime.fromISO(inputDateTime).toFormat("yyyy-MM-dd'T'HH:mm");
                                detail.deliveryDate = formattedDateTime;
                                console.log(formattedDateTime);
                            });
                    } else {
                        detail.deliveryDate = null;
                    }

                    if (detail.signedBy === undefined || detail.signedBy === null || detail.signedBy === '' || detail.signedBy.trim() === '') {
                        detail.signedBy = null;
                    }

                    if (line.isAsset && (detail.status.toLowerCase() == 'shipped' || detail.status.toLowerCase() == 'delivered' || detail.status.toLowerCase() == 'exception' || detail.status.toLowerCase() == 'refused')) {
                        // Check if serialNumber and assetTag are not empty
                        if (!detail.serialNumber) {
                            hasError = true;
                            shipStatus = detail.status;
                            serialIndex = lIndex;
                        }
                    }
                    if (line.isAsset && (detail.status.toLowerCase() == 'shipped')) {
                        if (!detail.assetTag && !isAssetChecked) {
                            hasError = true;
                            shipStatus = detail.status;
                            serialIndex = lIndex;
                        }
                    }

                    // clear asstet tag if toggled off
                    if (!line.isAsset) {
                        detail.assetTag = '';
                        detail.serialNumber = '';
                    }

                    if (isFirstDetail) {
                        const updatedDetail = {};
                        Object.entries(detail).forEach(([key, value]) => {
                            if (line.quantity > 1) {
                                if (value !== '') {
                                    updatedDetail[key] = key === 'quantity' ? parseInt(value) : value;
                                } else if (key === 'quantity') {
                                    updatedDetail[key] = 1; // Set default value to '1' if quantity is empty
                                }
                            } else {
                                updatedDetail[key] = key === 'quantity' ? parseInt(value) : value;
                            }
                        });
                        postData.orderLinesDetail.push({
                            orderLevelSequence: post.orderLevels[0].orderLevelSequence,
                            orderLineSequence: line.orderLineSequence,
                            lineDetailId: detail.lineDetailId,
                            extPoLineNumber: line.extPoLineNumber,
                            ...updatedDetail
                        });
                        isFirstDetail = false; // Set flag to false after processing the first detail
                    }

                    // console.log(qtyCnt);

                    if (qtyCnt > line.quantity) {
                        qtyErr = true;
                        itemIndex = lIndex;
                        itemNum = lIndex + 1;
                    }
                    if (qtyZero) {
                        itemIndex = lIndex;
                        itemNum = lIndex + 1;
                    }
                }
            });
        });



        if (qtyErr) {
            setLoadingIcon('');
            setErrorMessage('Combined Quantity cannot be more than Total Quantity - Item ' + itemNum);
            $('.qty-' + itemIndex).removeClass('bg-gray-200 text-gray-900 border-transparent').addClass('bg-rose-100 text-rose-500 border-rose-500');
        } else if (qtyZero) {
            setLoadingIcon('');
            setErrorMessage('Item Cannot have 0 Quantity - Item ' + itemNum);
            $('.qty-' + itemIndex).removeClass('bg-gray-200 text-gray-900 border-transparent').addClass('bg-rose-100 text-rose-500 border-rose-500');
        } else if (hasError) {
            setLoadingIcon('');
            setErrorMessage('Serial Number and Asset Tag are required for status: ' + shipStatus);
            $('.asset-' + serialIndex).removeClass('bg-gray-200 text-gray-900 border-transparent').addClass('bg-rose-100 text-rose-500 border-rose-500');
            $('.asset-checkbox-' + serialIndex).removeClass('hidden');
        } else {
            try {
                const response = await fetch(postUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${getToken()}`,
                    },
                    body: JSON.stringify(postData)
                });

                const data = await response.json();
                setLoadingIcon('');
                setSuccessMessage('Data saved successfully!');
                $('.asset-all').removeClass('bg-rose-100 text-rose-500 border-rose-500').addClass('bg-gray-200 text-gray-900 border-transparent');
                $('.asset-checkbox-wrap').addClass('hidden');
                fetchData();
            } catch (error) {
                console.error('POST request failed:', error);
            }
        }
    };

    const handleConfirm = () => {
        const disableUntil = Date.now() + 150 * 60 * 1000; // 150 minutes in milliseconds

        post.orderLevels[0].orderLines.forEach((line, lineIndex) => {
            line.lineDetails.forEach((detail) => {
                const currentStatus = detail.status;
                const initialStatus = initialStatuses[lineIndex][0];

                if (currentStatus !== initialStatus) {
                    localStorage.setItem(`${id}_${lineIndex}_${disableUntil}`, true);
                }
            });
        });

        setShowModal(false);
        handleSave();
    };

    const handleCancel = () => {
        setShowModal(false);
    };

    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => {
                setSuccessMessage('');
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    // get carrier dropdown list
    useEffect(() => {
        if (post && post.extCustomerMappingId && isAuthenticated) {
            const fetchCarrierData = async () => {
                try {
                    const response = await fetch(
                        `${carrierUrl}?countryCode=${post.extCustomerMappingId}`,
                        {
                            headers: {
                                Authorization: `Bearer ${getToken()}`,
                            },
                        }
                    );

                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }

                    const data = await response.json();
                    setCouriers(data.data);
                    setCourierLoader(true);
                } catch (error) {
                    console.error('Error fetching couriers:', error);
                }
            };

            fetchCarrierData();
        }
    }, [isAuthenticated, post, carrierUrl, getToken]);

    const handleAddLineDetail = (lineIndex) => {
        const updatedPost = { ...post };
        updatedPost.orderLevels[0].orderLines[lineIndex].lineDetails.push({
            addedRow: true
        });

        setPost(updatedPost);

    };

    const handleRemoveLineDetail = (lineIndex, detailIndex) => {
        const updatedPost = { ...post };
        post.orderLevels[0].orderLines[lineIndex].lineDetails.splice(detailIndex, 1);
        setPost(updatedPost);
    };

    useEffect(() => {
        if (post && post.poNumber) {
            document.title = `Global PO View - ${post.poNumber}`;
        }
    }, [post]);


    if (!post) {
        return <div className="spinner-container">
            <div className="loading-spinner">
            </div>
        </div>;
    }

    if (!post.orderLevels) {
        return <div className="spinner-container">
            <div className="loading-spinner">
            </div>
        </div>;
    }

    if (!courierLoader) {
        return (
            <div className="spinner-container">
                <div className="loading-spinner"></div>
            </div>
        );
    }

    return (
        <div className="relative shadow-md">
            <section className='cont-wrapper'>
                <nav className="border-gray-200 bg-z-blue dark:bg-blue-800 dark:border-gray-700 px-6">
                    <div className="flex flex-wrap items-center justify-between mx-auto p-4">
                        <a href="/" className="flex items-center">
                            <img src={require('../images/zones_logo_white.png')} style={{ height: '20px', marginRight: '25px', marginLeft: '5px' }} alt="Zones Logo" />
                            <span className="self-center text-3xl font-semibold text-white whitespace-nowrap dark:text-white">Purchase Order </span><span className="ml-4 self-center text-3xl text-white whitespace-nowrap dark:text-white">{post.poNumber}</span>
                            <p className={successMessage ? "fade-out" : "fa-hidden"}>{successMessage} <FontAwesomeIcon icon={faCheck} /></p>
                            <p className={errorMessage ? "show-err" : "fa-hidden"}>{errorMessage} <FontAwesomeIcon icon={faExclamationTriangle} /></p>
                            <p className={loadingIcon ? "loading" : "fa-hidden"}>{loadingIcon} <FontAwesomeIcon icon={faSpinner} pulse /></p>
                        </a>
                        <div className="flex md:order-2">
                            <Link to={`/`}><button type="button" className="text-white bg-slate-300 hover:bg-slate-200 focus:ring-2 focus:outline-none focus:ring-white font-medium rounded-lg text-md px-8 py-4 text-center mr-3 md:mr-0 dark:bg-white-600 dark:hover:bg-sky-300 dark:focus:ring-z-blue">Back</button></Link>
                            <Link to={`/edit/${id}`}><button type="button" className="text-white bg-slate-300 hover:bg-slate-200 focus:ring-2 focus:outline-none focus:ring-white font-medium rounded-lg text-md px-8 py-4 text-center mr-3 ml-8 md:mr-0 dark:bg-white-600 dark:hover:bg-sky-300 dark:focus:ring-z-blue">Edit Mappings</button></Link>
                            <button onClick={handleModalSave} type="button" className="text-white bg-emerald-400 hover:bg-emerald-300 focus:ring-2 focus:outline-none focus:ring-white font-medium rounded-lg text-md px-8 py-4 text-center mr-3 ml-8 md:mr-0 dark:bg-white-600 dark:hover:bg-blue-300 dark:focus:ring-z-blue">Save</button>
                            <ConfirmationModal show={showModal} onConfirm={() => handleConfirm(1)} onCancel={handleCancel} />
                        </div>
                    </div>
                </nav>

                <div className="grid grid-cols-6 gap-6 pt-6 pb-16">
                    <div className="px-12">
                        <img style={{ height: '115px', minWidth: '205px' }} src={require('../images/ServiceNowHeaderLogo.png')} alt="ServiceNow Logo" />
                        <h5 className="-mt-16 text-md font-semibold">{post.extCustomerId + ' ' + post.extCustomerMappingId}</h5>
                    </div>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div className="pr-12 pr-mob">
                        <h3>Purchase Order</h3>
                        <span>Request ID: <strong>{post.poNumber}</strong></span><br />
                        <span>PO Number: <strong>{post.customerReference}</strong></span><br />
                        <span>Date: <strong>{DateTime.fromISO(post.createdDate).toFormat('MMM dd t')}</strong></span>
                    </div>
                </div>
                <div className="relative">
                    {post.orderLevels && post.orderLevels.length > 0 ?
                        <div className="bg-gray-50 dark:bg-gray-700 font-medium pt-6 pb-8 px-12">
                            <h6 className="font-bold uppercase">Shipment #1</h6>
                            <span>{post.orderLevels[0].shipToFirstName + ' ' + post.orderLevels[0].shipToLastName}</span><br />
                            <span>{post.orderLevels[0].shipToAddressLine1 + ' ' + post.orderLevels[0].shipToAddressLine2}</span><br />
                            <span>{post.orderLevels[0].shipToRegion + ' ' + post.orderLevels[0].shipToCity}</span><br />
                            <span>{post.orderLevels[0].shipToCountry + ' - ' + post.orderLevels[0].shipToPostalCode}</span><br />
                            <span>{post.orderLevels[0].shipToEmail}</span><br />
                            <span>{post.orderLevels[0].shipToPhone}</span><br />
                        </div>
                        : <div className="bg-gray-50 dark:bg-gray-700 font-medium pt-6 pb-8 px-12">
                            <h6 className="font-bold uppercase">No Order Details</h6>
                        </div>
                    }
                </div>
            </section>
            <section className="table-wrap shadow-md sm:rounded-lg">
                {post.orderLevels && post.orderLevels.length ?
                    <table className="text-md text-left text-gray-500 dark:text-gray-400">
                        <thead className="text-md text-z-blue uppercase bg-gray-100 dark:bg-blue-700 dark:text-white">
                            <tr>
                                <th scope="col" className="pl-12 pr-0 py-4 w-2/12">Item</th>
                                <th scope="col" className="px-8 py-4 w-1/12">Part #</th>
                                <th scope="col" className="pl-12 pr-8 py-4 w-1/12">Qty.</th>
                                <th scope="col" className="px-12 py-4 w-8/12">Line Details</th>
                            </tr>
                        </thead>
                        <tbody className={`${post.orderLevels[0].orderLines.some(line => line.quantity > 1) ? 'qty-field' : ''}`}>
                            {post.orderLevels && post.orderLevels[0].orderLines.map((line, lineIndex) => (
                                <tr key={line.orderLineSequence} className="bg-white border-b border-gray-100 dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
                                    <td className="pl-12 pr-0 py-6">{lineIndex + 1 + '. ' + line.customerProductDescription}<br />
                                        <span className="font-semibold text-lg ml-6">{line.lineComments[0].key + ': ' + line.lineComments[0].value}</span>
                                    </td>
                                    <td className="px-8 py-6">{line.customerPartNumber}</td>
                                    <td className="pl-12 pr-8 py-6">
                                        {line.quantity}
                                        {lineIndex === 0 && post.orderLevels[0].orderLines.length > 1 && (
                                            <div className='float-right'>
                                                <button onClick={handleCopyValues} className='copy-btn' title='Click to copy all Line Details values from this row to the below rows'>
                                                    <i className='fa fa-copy'></i>
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                    <td className="pl-12 pr-6 py-6">
                                        {line.lineDetails && line.lineDetails.length > 0 ? (
                                            line.lineDetails.slice(0, line.quantity).map((detail, detailIndex) => (
                                                <div key={count++} className="input-cont">
                                                    <div className='input-wrap asset-tag-wrap'>
                                                        <label className="block uppercase tracking-wide text-gray-700 text-lg font-bold">Serial #</label>
                                                        {line.isAsset ? <input type="text" className={`asset-${lineIndex} asset-all appearance-none block w-full bg-gray-200 text-gray-700 border border-transparent rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white`} name="serialNumber" value={detail.serialNumber || ''} onChange={(e) => handleChange(e, lineIndex, detailIndex)} /> : <input type="hidden" />}
                                                    </div>
                                                    <div className='input-wrap asset-tag-wrap'>
                                                        <label className="block uppercase tracking-wide text-gray-700 text-lg font-bold">Asset Tag</label>
                                                        {line.isAsset ? <input type="text" className={`asset-${lineIndex} asset-all appearance-none block w-full bg-gray-200 text-gray-700 border border-transparent rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white`} name="assetTag" value={detail.assetTag || ''} onChange={(e) => handleChange(e, lineIndex, detailIndex)} /> : <input type="hidden" />}
                                                        <span className={`asset-checkbox-${lineIndex} asset-checkbox-wrap hidden`}>
                                                            <input
                                                                type="checkbox"
                                                                className="asset-checkbox"
                                                                checked={!!isAssetChecked[lineIndex]}
                                                                onChange={(e) => handleChange(e, lineIndex, detailIndex)}
                                                            />
                                                            Leave Blank
                                                        </span>
                                                    </div>
                                                    <div className='input-hid'>
                                                        <input type="text" className='v-hidden' />
                                                        {line.quantity > 1 ? <input type="number" name="quantity" className={line.quantity === 0 ? `qty-${lineIndex} bg-white relative w-full text-rose-500 border-2 border-rose-500 rounded-lg focus:outline-rose-500 focus:border-rose-500 block p-2.5` : `qty-${lineIndex} qty-split bg-gray-200 relative text-gray-900 border-2 border-transparent rounded-lg focus:outline-z-blue focus:border-z-blue p-2.5`} value={!line.lineDetails[1] ? line.quantity : (detail.quantity || '1')} onChange={(e) => handleChange(e, lineIndex, detailIndex)} /> : <input type="text" className='v-hidden' />}
                                                    </div>
                                                    {/* <div className='input-wrap relative'>
                                            <label className="block uppercase tracking-wide text-gray-700 text-lg font-bold">Status</label>
                                            <select className="block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500" name="status" value={detail.status} onChange={(e) => handleChange(e, lineIndex, detailIndex)}>
                                                <option value="Received">Received</option>
                                                <option value="InProcess">In Process</option>
                                                <option value="Shipped">Shipped</option>
                                                <option value="Delivered">Delivered</option>
                                                <option value="Exception">Exception</option>
                                                <option value="Refused">Refused</option>
                                                <option value="Cancelled">Cancelled</option>
                                            </select>
                                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-12 pt-9 text-gray-700">
                                                <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                                            </div>
                                        </div> */}
                                                    <div className='input-wrap relative'>
                                                        <label className="block uppercase tracking-wide text-gray-700 text-lg font-bold">Status</label>
                                                        <div key={`${lineIndex}-${detailIndex}`}>
                                                            <StatusDropdown
                                                                initialStatus={detail.status}
                                                                lineIndex={lineIndex}
                                                                detailIndex={detailIndex}
                                                                handleChange={handleChange}
                                                                disabled={!!disabledLines[lineIndex]}
                                                            />
                                                            <span className='counter-span'>{disabledLines[lineIndex] ? `This status dropdown is disabled for ${Math.ceil(disabledTimes[lineIndex] / 60000)} minutes` : ''}</span>
                                                        </div>
                                                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-12 pt-9 text-gray-700">
                                                            <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                                                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                                                            </svg>
                                                        </div>
                                                    </div>
                                                    <div className='input-wrap'>
                                                        <label className="block uppercase tracking-wide text-gray-700 text-lg font-bold">Tracking #</label>
                                                        <input type="text" className="appearance-none block w-full bg-gray-200 text-gray-700 border border-transparent rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white" name="trackingNumber" value={detail.trackingNumber || ''} onChange={(e) => handleChange(e, lineIndex, detailIndex)} />
                                                    </div>
                                                    <div className='input-wrap relative'>
                                                        <label className="block uppercase tracking-wide text-gray-700 text-lg font-bold">Carrier</label>
                                                        <select className="appearance-none block w-full bg-gray-200 text-gray-700 border border-transparent rounded py-3 px-4 pr-8 mb-3 leading-tight focus:outline-none focus:bg-white focus:border-gray-500" name="carrier" value={detail.carrier} onChange={(e) => handleChange(e, lineIndex, detailIndex)}>
                                                            <option value="">Select Carrier</option>
                                                            {Array.isArray(couriers) && couriers.map(courier => (
                                                                <option key={courier.code} value={courier.code}>
                                                                    {courier.name}
                                                                </option>
                                                            ))}
                                                            <option value="other">Other</option>
                                                        </select>
                                                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-12 pt-9 text-gray-700">
                                                            <svg className="carrier-svg fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg>
                                                        </div>
                                                    </div>
                                                    <div className='input-wrap iw-date'>
                                                        <label className="block uppercase tracking-wide text-gray-700 text-lg font-bold">Delivery Time</label>
                                                        <input type="datetime-local" className="appearance-none block w-full bg-gray-200 text-gray-700 border border-transparent rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white" name="deliveryDate" value={detail.deliveryDate || ''} onChange={(e) => handleChange(e, lineIndex, detailIndex)} />
                                                    </div>
                                                    <div className='input-wrap'>
                                                        <label className="block uppercase tracking-wide text-gray-700 text-lg font-bold">Signed By</label>
                                                        <input type="text" className="appearance-none block w-full bg-gray-200 text-gray-700 border border-transparent rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white" name="signedBy" value={detail.signedBy || ''} onChange={(e) => handleChange(e, lineIndex, detailIndex)} />
                                                    </div>

                                                    <div className='input-wrap add-btn'>
                                                        {!detail.lineDetailId && detailIndex > 0 ? <button className="bg-rose-500 hover:bg-rose-400" onClick={() => handleRemoveLineDetail(lineIndex, detailIndex)}><FontAwesomeIcon icon={faMinus} /></button> : ''}
                                                        {line.quantity > 1 && detailIndex == 0 ? <button className="bg-z-blue hover:bg-z-blue-hov" onClick={() => handleAddLineDetail(lineIndex)}><FontAwesomeIcon icon={faPlus} /></button> : ''}
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div key={count++} className="input-cont">
                                                <div className='input-wrap asset-tag-wrap'>
                                                    <label className="block uppercase tracking-wide text-gray-700 text-lg font-bold">Serial #</label>
                                                    {line.isAsset ? <input type="text" className="appearance-none block w-full bg-gray-200 text-gray-700 border border-transparent rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white" name="serialNumber" value="" disabled={line.quantity === 0} onChange={(e) => handleChange(e, lineIndex, 0)} /> : <input type="hidden" />}
                                                </div>
                                                <div className='input-wrap asset-tag-wrap'>
                                                    <label className="block uppercase tracking-wide text-gray-700 text-lg font-bold">Asset Tag</label>
                                                    {line.isAsset ? <input type="text" className="appearance-none block w-full bg-gray-200 text-gray-700 border border-transparent rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white" name="assetTag" value="" disabled={line.quantity === 0} onChange={(e) => handleChange(e, lineIndex, 0)} /> : <input type="hidden" />}
                                                </div>
                                                <div className='input-hid'><input type="text" className='v-hidden' /></div>
                                                <div className='input-wrap relative'>
                                                    <label className="block uppercase tracking-wide text-gray-700 text-lg font-bold">Status</label>
                                                    <select className="block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500" name="status" value="" disabled={line.quantity === 0} onChange={(e) => handleChange(e, lineIndex, 0)}>
                                                        <option value="Received">Received</option>
                                                        <option value="InProcess">In Process</option>
                                                        <option value="Cancelled">Cancelled</option>
                                                        {/* <option value="Shipped">Shipped</option>
                                            <option value="Delivered">Delivered</option>
                                            <option value="Exception">Exception</option>
                                            <option value="Refused">Refused</option> */}
                                                    </select>
                                                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-12 pt-9 text-gray-700">
                                                        <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg>
                                                    </div>
                                                </div>
                                                <div className='input-wrap'>
                                                    <label className="block uppercase tracking-wide text-gray-700 text-lg font-bold">Tracking #</label>
                                                    <input type="text" className="appearance-none block w-full bg-gray-200 text-gray-700 border border-transparent rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white" name="trackingNumber" value="" disabled={line.quantity === 0} onChange={(e) => handleChange(e, lineIndex, 0)} />
                                                </div>
                                                <div className='input-wrap relative'>
                                                    <label className="block uppercase tracking-wide text-gray-700 text-lg font-bold">Carrier</label>
                                                    <select className="appearance-none block w-full bg-gray-200 text-gray-700 border border-transparent rounded py-3 px-4 pr-8 mb-3 leading-tight focus:outline-none focus:bg-white focus:border-gray-500" name="carrier" value="" disabled={line.quantity === 0} onChange={(e) => handleChange(e, lineIndex, 0)}>
                                                        <option value="">Select Carrier</option>
                                                        {Array.isArray(couriers) && couriers.map(courier => (
                                                            <option key={courier.code} value={courier.code}>
                                                                {courier.name}
                                                            </option>
                                                        ))}
                                                        <option value="other">Other</option>
                                                    </select>
                                                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-12 pt-9 text-gray-700">
                                                        <svg className="carrier-svg fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg>
                                                    </div>
                                                </div>
                                                <div className='input-wrap iw-date'>
                                                    <label className="block uppercase tracking-wide text-gray-700 text-lg font-bold">Delivery Time</label>
                                                    <input type="datetime-local" className="appearance-none block w-full bg-gray-200 text-gray-700 border border-transparent rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white" name="deliveryDate" value="" disabled={line.quantity === 0} onChange={(e) => handleChange(e, lineIndex, 0)} />
                                                </div>
                                                <div className='input-wrap'>
                                                    <label className="block uppercase tracking-wide text-gray-700 text-lg font-bold">Signed By</label>
                                                    <input type="text" className="appearance-none block w-full bg-gray-200 text-gray-700 border border-transparent rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white" name="signedBy" value="" disabled={line.quantity === 0} onChange={(e) => handleChange(e, lineIndex, 0)} />
                                                </div>

                                                {line.lineDetails.map((detail, detailIndex) => (
                                                    <div className='input-wrap add-btn'>
                                                        {!detail.lineDetailId ? <button className="bg-rose-500 hover:bg-rose-400" onClick={() => handleRemoveLineDetail(lineIndex, detailIndex)}><FontAwesomeIcon icon={faMinus} /></button> : ''}
                                                        {line.quantity > 1 && detailIndex == 0 ? <button className="bg-z-blue hover:bg-z-blue-hov" onClick={() => handleAddLineDetail(lineIndex)}><FontAwesomeIcon icon={faPlus} /></button> : ''}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    : <div></div>}
            </section>
        </div>
    );
};

export default Post;